import { graphHooks } from '@tunasong/graph-lib/react'
import { Suggestion as NudgeIcon } from '@tunasong/icons'
import { logger } from '@tunasong/models'
import type { Agent, Comment, Persisted } from '@tunasong/schemas'
import { IconButton, Menu } from '@tunasong/ui-lib'
import type { FC, MouseEvent } from 'react'
import { useMemo, useState } from 'react'
import { Node } from 'slate'

export interface NudgeBellProps {
  agent: Persisted<Agent>
  className?: string
}

interface NudgeComment extends Persisted<Comment> {
  metadata?: {
    [key: string]: unknown
    agentNudge?: boolean
    title?: string
    readAt?: string
  }
}

const isNudge = (c: Persisted<Comment>): c is NudgeComment =>
  typeof c.metadata === 'object' && c.metadata !== null && (c.metadata as { agentNudge?: boolean }).agentNudge === true

const BODY_CLIP = 280

const nudgeBody = (nudge: NudgeComment): string => {
  try {
    const text = Node.string(nudge as unknown as Node).trim()
    if (!text) {
      return '(empty suggestion)'
    }
    return text.length > BODY_CLIP ? `${text.slice(0, BODY_CLIP)}…` : text
  } catch {
    return '(unreadable suggestion)'
  }
}

export const NudgeBell: FC<NudgeBellProps> = ({ agent, className }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [locallyDismissed, setLocallyDismissed] = useState<Set<string>>(new Set())
  const { entities: children } = graphHooks.useEntityChildren<Persisted<Comment>>({
    parentId: agent.id,
  })
  const update = graphHooks.useEntityUpdate()

  const nudges: NudgeComment[] = useMemo(() => children.filter(isNudge), [children])
  const isRead = (n: NudgeComment) => locallyDismissed.has(n.id) || Boolean(n.metadata?.readAt)
  const unread = useMemo(() => nudges.filter(n => !isRead(n)), [nudges, locallyDismissed])

  const handleOpen = (ev: MouseEvent<HTMLElement>) => setAnchor(ev.currentTarget)
  const handleClose = () => setAnchor(null)
  const handleDismiss = (nudge: NudgeComment) => () => {
    setLocallyDismissed(prev => {
      if (prev.has(nudge.id)) {
        return prev
      }
      const next = new Set(prev)
      next.add(nudge.id)
      return next
    })
    logger.debug('NudgeBell: marking nudge as read', { nudgeId: nudge.id })
    void update(nudge.id, { metadata: { ...nudge.metadata, readAt: new Date().toISOString() } })
  }

  if (unread.length === 0) {
    return null
  }

  return (
    <div className={className}>
      <IconButton aria-label={`Agent suggestions (${unread.length} unread)`} onClick={handleOpen} size="small">
        <NudgeIcon />
        {unread.length > 0 ? <span>{unread.length}</span> : null}
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={handleClose}>
        {unread.map((nudge: NudgeComment) => (
          <div
            key={nudge.id}
            role="menuitem"
            tabIndex={0}
            onClick={handleDismiss(nudge)}
            style={{
              padding: '6px 16px',
              cursor: 'pointer',
              whiteSpace: 'normal',
              maxWidth: 'var(--nudge-max-width, 420px)',
            }}
          >
            {nudgeBody(nudge)}
          </div>
        ))}
      </Menu>
    </div>
  )
}
